import { Location } from '@angular/common';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SubMenuService } from '../../../services/submenu.service';

/** The screens Skipper knows, in the `screenId` vocabulary the tours share. */
export type SkipperScreenId = 'home_screen' | 'chat_list_screen' | 'community_screen' | 'profile_screen';

/** Route-data key: `data: { [SKIPPER_SCREEN_KEY]: 'home_screen' }`. */
export const SKIPPER_SCREEN_KEY = 'skipperScreenId';

/**
 * Which screen the user is standing on, for Skipper's greeting and search bias.
 *
 * Read from route data — declared on the route, not sniffed from the URL.
 *
 * One surface changes without the router: the profile panel opens over Home
 * and switches tabs with `location.replaceState`. While it is open, the tab it
 * writes into the address bar decides the screen (Dockmates and Fleets are
 * Community; the rest are Profile) — followed through `Location.onUrlChange`,
 * which `replaceState` fires, so the panel itself needs no Skipper code.
 */
@Injectable({ providedIn: 'root' })
export class SkipperScreenService {
  private router = inject(Router);
  private location = inject(Location);
  private submenu = inject(SubMenuService);
  private profileOpen = false;

  private readonly fromRoute = signal<SkipperScreenId | null>(this.read());
  private readonly override = signal<SkipperScreenId | null>(null);

  readonly screenId = computed(() => this.override() ?? this.fromRoute());

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.fromRoute.set(this.read()));

    this.submenu.userProfileOpenState$.pipe(takeUntilDestroyed()).subscribe((open) => {
      this.profileOpen = open;
      this.syncProfilePanel();
    });
    const stopUrl = this.location.onUrlChange(() => this.syncProfilePanel());
    inject(DestroyRef).onDestroy(stopUrl);
  }

  private syncProfilePanel(): void {
    if (!this.profileOpen) {
      this.override.set(null);
      return;
    }
    const tab = this.location.path().split(/[/?#]/)[1] ?? '';
    this.override.set(tab === 'dockmates' || tab === 'fleets' ? 'community_screen' : 'profile_screen');
  }

  /** The deepest route that declares a screen wins. */
  private read(): SkipperScreenId | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let found: SkipperScreenId | null = null;
    while (route) {
      const id = route.data[SKIPPER_SCREEN_KEY] as SkipperScreenId | undefined;
      if (id) found = id;
      route = route.firstChild;
    }
    return found;
  }
}
